import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { RefObject } from "react";
import * as THREE from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";

import { useThree } from "@react-three/fiber";

import { CameraPosition } from "./types";

interface UseCameraControllerParams {
  /** Reference to orbit controls for camera manipulation */
  orbitRef: RefObject<OrbitControlsImpl>;
  /** Camera position preset */
  cameraPosition: CameraPosition;
  /** Callback to set positioning flag */
  setIsPositioningCamera: Dispatch<SetStateAction<boolean>>;
}

interface UseCameraControllerReturn {
  /** Whether the camera has been positioned */
  hasPositioned: boolean;
}

/**
 * Custom hook that positions the camera at the top altitude of the model
 * looking horizontally at the center of gravity
 *
 * The camera will be positioned at the same Y level as the top of the object,
 * but at a distance on the Z axis, looking towards the center of gravity
 *
 * @param params - Hook parameters with orbit controls reference and camera position
 * @returns Object containing positioning state
 */
export function useCameraController({
  orbitRef,
  cameraPosition,
  setIsPositioningCamera,
}: UseCameraControllerParams): UseCameraControllerReturn {
  const { camera, scene, size: viewport } = useThree();
  const [hasPositioned, setHasPositioned] = useState(false);
  const positioningTimeoutRef = useRef<NodeJS.Timeout>();
  const flagClearTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Wait for orbit controls to be available and scene to have content
    if (!orbitRef.current || hasPositioned || cameraPosition === "manual")
      return;

    // Clear any existing timeout
    if (positioningTimeoutRef.current) {
      clearTimeout(positioningTimeoutRef.current);
    }

    // Add a small delay to ensure the model is fully loaded and processed
    positioningTimeoutRef.current = setTimeout(() => {
      if (!orbitRef.current) return;

      // Calculate bounding box of the entire scene to find the model
      const box = new THREE.Box3().setFromObject(scene);
      let size: THREE.Vector3;
      let center: THREE.Vector3;

      // Prefer using the bounding box from the processed model (ModelRoot) if available
      const modelRoot = scene.getObjectByName("ModelRoot");
      if (modelRoot && (modelRoot as any).userData.boundingBox) {
        const bb = (modelRoot as any).userData.boundingBox as {
          size: THREE.Vector3;
          center: THREE.Vector3;
        };
        size = bb.size;
        center = bb.center;
      } else {
        size = box.getSize(new THREE.Vector3());
        center = box.getCenter(new THREE.Vector3());
      }

      // Skip positioning if the model is too small (likely not loaded yet or empty scene)
      if (size.length() < 0.1) {
        return;
      }

      // Compute distance for optimal framing
      const perspectiveCamera = camera as THREE.PerspectiveCamera;
      const vFov = THREE.MathUtils.degToRad(perspectiveCamera.fov ?? 75);
      const aspect = viewport.width / viewport.height;
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);

      const maxExtent = Math.max(size.x, size.y, size.z) * 0.9; // Extra padding to ensure full object visibility
      const distVertical = maxExtent / Math.tan(vFov / 2);
      const distHorizontal = maxExtent / Math.tan(hFov / 2);
      const distance = Math.max(distVertical, distHorizontal);

      // Position camera based on selected preset
      let cameraPos: THREE.Vector3;
      const lookAtPos: THREE.Vector3 = new THREE.Vector3(
        center.x,
        center.y,
        center.z
      );

      switch (cameraPosition) {
        case "front":
          cameraPos = new THREE.Vector3(
            center.x,
            center.y,
            center.z + distance
          );
          break;
        case "top":
          cameraPos = new THREE.Vector3(
            center.x,
            center.y + distance,
            center.z
          );
          break;
        case "left":
          cameraPos = new THREE.Vector3(
            center.x - distance,
            center.y,
            center.z
          );
          break;
        case "right":
          cameraPos = new THREE.Vector3(
            center.x + distance,
            center.y,
            center.z
          );
          break;
        case "default":
        default:
          // Default: 75% height looking horizontally at center
          cameraPos = new THREE.Vector3(
            center.x,
            center.y + size.y * 0.25,
            center.z + distance
          );
          break;
      }

      camera.position.copy(cameraPos);
      camera.lookAt(lookAtPos);

      // Set orbit controls target to the center of gravity
      orbitRef.current.target.copy(center);
      orbitRef.current.update();

      setHasPositioned(true);

      flagClearTimeoutRef.current = setTimeout(() => {
        setIsPositioningCamera(false);
      }, 1_000);
    }, 100); // Small delay to ensure model is processed

    return () => {
      if (positioningTimeoutRef.current) {
        clearTimeout(positioningTimeoutRef.current);
      }
      if (flagClearTimeoutRef.current) {
        clearTimeout(flagClearTimeoutRef.current);
      }
    };
  }, [
    camera,
    scene,
    orbitRef,
    hasPositioned,
    viewport.width,
    viewport.height,
    cameraPosition,
    setIsPositioningCamera,
  ]);

  // Separate effect to clear positioning flag after successful positioning
  // This ensures the flag gets cleared even if the main effect early returns
  useEffect(() => {
    if (hasPositioned && cameraPosition !== "manual") {
      const timeoutId = setTimeout(() => {
        setIsPositioningCamera(false);
      }, 500); // Clear flag 500ms after positioning is complete

      return () => clearTimeout(timeoutId);
    }
  }, [hasPositioned, cameraPosition, setIsPositioningCamera]);

  // Reset positioning when component unmounts or when a new model might be loaded
  useEffect(() => {
    return () => {
      setHasPositioned(false);
      // Clean up any pending timeouts
      if (positioningTimeoutRef.current) {
        clearTimeout(positioningTimeoutRef.current);
      }
      if (flagClearTimeoutRef.current) {
        clearTimeout(flagClearTimeoutRef.current);
      }
    };
  }, []);

  // Reset positioning when camera position changes
  useEffect(() => {
    if (cameraPosition !== "manual") {
      setHasPositioned(false);
    }
  }, [cameraPosition]);

  return { hasPositioned };
}

/**
 * Wrapper component that uses the camera controller hook
 * This allows the hook to be used within the Canvas context
 *
 * Otherwise we face the `Error: R3F: Hooks can only be used within the Canvas component` error
 */
export function CameraController(props: UseCameraControllerParams) {
  useCameraController(props);
  return null;
}
